// 렌더 품질: 고정 프리셋(?quality, 설정 화면)과 자동 품질 조절(프레임 시간 기반 단계 사다리).
// 우선순위: 명시 옵션(?quality) → UI 설정(UiPrefs, auto가 아니면) → 자동(기억된 단계 → GPU·화면 크기로 고른 첫 단계).
// 자동일 때만 QualityGovernor가 최근 2초 프레임 시간 p90을 보고 사다리를 오르내린다. 프레임 루프 안에서 객체를 만들지 않는다.
import { RENDER_QUALITY, type QualityLevel, type QualityStep } from '../shared/config';
import { loadPrefs } from '../ui/prefs';

export type { QualityStep };
export type QualitySpec = QualityStep;

const isLevel = (v: string | null | undefined): v is QualityLevel => v === 'low' || v === 'mid' || v === 'high';

/**
 * 자동 조절 사다리(0이 최고). 낮추는 순서(docs/COMBAT-OVERHAUL.md, opus-perf 지시서):
 * 해상도 배율 2→1.5→1.25→1 → 그림자 해상도 → 그림자 끄기(블롭) → 블룸 → 몹 LOD 거리·외곽선 → 환경 장식.
 * 배율 0.85(화면 픽셀보다 낮은 해상도)는 모든 것이 흐려지므로 마지막 수단으로 맨 아래에 두고,
 * 렌더 JS 시간이 프레임 시간의 대부분인 CPU 병목이면 쓰지 않는다(픽셀을 줄여도 빨라지지 않는다).
 * MSAA는 부팅 때만 정한다(첫 단계 R0~R2면 켬, R3 이하면 끔). 실행 중 표본 수를 바꾸면 모든 렌더 파이프라인이 다시 만들어져
 * CPU 4배 감속 측정에서 WebGPU 2.1~2.4초, WebGL2 5.7초 멈췄다(qa/perf/ab, 2026-10-14). 그림자 파이프라인 유무도 같은 이유로 부팅 때만 정한다.
 * env는 부팅 때 환경 배치 밀도·입자 수를 고르는 이름표다(환경은 실행 중 다시 배치하지 않는다).
 */
const H = RENDER_QUALITY.high;
export const QUALITY_LADDER: readonly QualityStep[] = [
  { ...H, label: 'R0 high' },
  { ...H, label: 'R1 dpr1.5', pixelRatio: 1.5 },
  { ...H, label: 'R2 dpr1.25', pixelRatio: 1.25 },
  { ...H, label: 'R3 dpr1', pixelRatio: 1, msaa: false, env: 'mid' },
  { ...H, label: 'R4 shadow1024', pixelRatio: 1, msaa: false, shadowMap: 1024, env: 'mid' },
  { ...H, label: 'R5 blob', pixelRatio: 1, msaa: false, shadowMap: 0, env: 'mid' },
  { ...H, label: 'R6 bloom.5', pixelRatio: 1, shadowMap: 0, msaa: false, bloomScale: 0.5, env: 'mid' },
  { ...H, label: 'R7 lod', pixelRatio: 1, shadowMap: 0, msaa: false, bloomScale: 0.5, mobLodNear: 20, mobHdCap: 20, mobOutline: 'near', env: 'low', particleScale: 0.5 },
  { ...H, label: 'R8 lod+', pixelRatio: 1, shadowMap: 0, msaa: false, bloomScale: 0.5, mobLodNear: 16, mobHdCap: 10, mobOutline: 'off', env: 'low', particleScale: 0.5 },
  // 가장 가까운 6마리는 원본으로 남긴다: 새 카메라(피치 28°)는 앞줄 몹이 화면 아래에 크게 잡혀 저폴리의 각진 면이 바로 보인다
  { ...H, label: 'R9 lean', pixelRatio: 1, shadowMap: 0, msaa: false, bloomScale: 0.5, mobLodNear: 12, mobHdCap: 6, mobOutline: 'off', kaelOutline: false, decor: false, env: 'low', particleScale: 0.5 },
  { ...H, label: 'R10 floor', pixelRatio: 0.85, shadowMap: 0, msaa: false, bloomScale: 0.5, mobLodNear: 12, mobHdCap: 6, mobOutline: 'off', kaelOutline: false, decor: false, env: 'low', particleScale: 0.5 },
];
/** 해상도만 낮추는 맨 아래 단계(CPU 병목이면 건너뛴다) */
const GPU_ONLY_STEP = 10;
/** 렌더 JS 시간 중앙값이 프레임 시간 중앙값의 이 비율 이상이면 CPU 병목으로 본다 */
const CPU_BOUND_RATIO = 0.6;
/** 기기 등급별 첫 단계(기억된 단계가 없을 때) */
export const START_STEP: Record<QualityLevel, number> = { high: 0, mid: 3, low: 8 };

export interface QualityPlan {
  /** 'auto'면 조절기가 돈다 */
  mode: 'auto' | 'fixed';
  /** 고정 프리셋 이름 또는 자동 첫 단계의 env 이름표 */
  level: QualityLevel;
  /** 자동: 첫 사다리 단계 */
  step: number;
  spec: QualityStep;
  /** 첫 단계를 고른 이유(QA) */
  reason: string;
}

/** 고정 프리셋 결정: 명시 옵션 → ?quality → UI 설정. 없으면 null(자동). */
export function fixedQuality(requested?: QualityLevel | 'auto'): QualityLevel | null {
  if (isLevel(requested)) return requested;
  const url = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('quality') : null;
  if (isLevel(url)) return url;
  const pref = loadPrefs().quality;
  if (isLevel(pref)) return pref;
  return null;
}

/** 예전 호출부 호환: 고정 프리셋이 없으면 터치 기기 low, 그 외 high. */
export function resolveQuality(requested?: QualityLevel | 'auto'): QualityLevel {
  const f = fixedQuality(requested);
  if (f) return f;
  return isCoarse() ? 'low' : 'high';
}

const isCoarse = (): boolean => typeof matchMedia !== 'undefined' && matchMedia('(pointer: coarse)').matches;

// ── GPU 정보와 첫 단계 ──────────────────────────────────

export interface GpuInfo {
  backend: string;
  vendor: string;
  renderer: string;
  /** 'discrete' | 'integrated' | 'mobile' | 'software' | 'unknown' */
  tier: string;
}

/** WebGPU adapter info 또는 WebGL WEBGL_debug_renderer_info에서 GPU 이름을 읽는다(부팅 때 한 번). */
export function readGpuInfo(three: unknown, backend: string): GpuInfo {
  let vendor = '', renderer = '';
  try {
    const be = (three as { backend?: { device?: { adapterInfo?: { vendor?: string; architecture?: string; description?: string; device?: string } }; gl?: WebGL2RenderingContext } }).backend;
    const ai = be?.device?.adapterInfo;
    if (ai) {
      vendor = ai.vendor ?? '';
      renderer = [ai.architecture, ai.description, ai.device].filter(Boolean).join(' ');
    }
    const gl = be?.gl;
    if (gl && !renderer) {
      const ext = gl.getExtension('WEBGL_debug_renderer_info');
      vendor = String(ext ? gl.getParameter(ext.UNMASKED_VENDOR_WEBGL) : gl.getParameter(gl.VENDOR));
      renderer = String(ext ? gl.getParameter(ext.UNMASKED_RENDERER_WEBGL) : gl.getParameter(gl.RENDERER));
    }
  } catch {
    /* 정보 없음 */
  }
  return { backend, vendor, renderer, tier: classifyGpu(vendor, renderer) };
}

/** GPU 이름으로 등급을 나눈다(보수적으로: 모르면 unknown = high 취급, 조절기가 맞춘다). */
export function classifyGpu(vendor: string, renderer: string): string {
  const s = (vendor + ' ' + renderer).toLowerCase();
  if (/swiftshader|llvmpipe|softpipe|basic render|software/.test(s)) return 'software';
  if (/mali|adreno|powervr|imagination|qualcomm|apple a\d|xclipse|videocore|\barm\b/.test(s)) return 'mobile';
  if (/radeon\(tm\) graphics|radeon graphics|vega \d+ graphics|uhd|iris|hd graphics|apple m\d|apple gpu|integrated/.test(s)) return 'integrated';
  if (/nvidia|geforce|rtx|gtx|quadro|radeon|\barc\b|amd/.test(s)) return 'discrete';
  if (/intel/.test(s)) return 'integrated';
  return 'unknown';
}

/**
 * 첫 단계: 모바일·소프트웨어 GPU 또는 터치 기기는 low, 내장 GPU는 mid, 그 밖은 high.
 * 그다음 실제 그릴 픽셀(CSS 크기 × min(DPR, 단계 배율)²)이 기준을 넘지 않을 때까지 해상도 단계를 내린다(배율 1까지).
 */
export function pickStartStep(gpu: GpuInfo, cssW: number, cssH: number, dpr: number): { step: number; reason: string } {
  const coarse = isCoarse();
  let cls: QualityLevel = 'high';
  if (gpu.tier === 'mobile' || gpu.tier === 'software' || coarse) cls = 'low';
  else if (gpu.tier === 'integrated') cls = 'mid';
  let step = START_STEP[cls];
  // 픽셀 예산: high 약 500만(1440p 정도), 그 밖은 배율 1부터 시작
  const px = (s: number): number => cssW * cssH * Math.min(dpr, QUALITY_LADDER[s].pixelRatio) ** 2;
  while (step < 3 && px(step) > 5.0e6) step++;
  return { step, reason: 'gpu=' + gpu.tier + (coarse ? ' coarse' : '') + ' class=' + cls + ' px=' + Math.round(px(step)) };
}

// ── 기억 ──────────────────────────────────────────────

export const PERF_STORE_KEY = 'dungeonhero.perf.v1';

interface Stored { [backend: string]: { step: number; at: number } }

export function loadStoredStep(backend: string): number | null {
  try {
    const raw = typeof localStorage !== 'undefined' ? localStorage.getItem(PERF_STORE_KEY) : null;
    if (!raw) return null;
    const j = JSON.parse(raw) as Stored;
    const e = j[backend];
    if (e && Number.isInteger(e.step) && e.step >= 0 && e.step < QUALITY_LADDER.length) return e.step;
  } catch {
    /* 무시 */
  }
  return null;
}

export function saveStoredStep(backend: string, step: number): void {
  try {
    if (typeof localStorage === 'undefined') return;
    const raw = localStorage.getItem(PERF_STORE_KEY);
    const j = (raw ? JSON.parse(raw) : {}) as Stored;
    j[backend] = { step, at: Date.now() };
    localStorage.setItem(PERF_STORE_KEY, JSON.stringify(j));
  } catch {
    /* 무시 */
  }
}

/** 부팅 계획: 고정 프리셋이면 그대로, 자동이면 기억된 단계 → GPU·화면 기준 첫 단계. */
export function planQuality(requested: QualityLevel | 'auto' | undefined, gpu: GpuInfo, cssW: number, cssH: number, dpr: number): QualityPlan {
  const fixed = fixedQuality(requested);
  if (fixed) return { mode: 'fixed', level: fixed, step: -1, spec: RENDER_QUALITY[fixed], reason: 'fixed ' + fixed };
  const stored = loadStoredStep(gpu.backend);
  const pick = stored !== null ? { step: stored, reason: 'stored step ' + stored } : pickStartStep(gpu, cssW, cssH, dpr);
  const spec = QUALITY_LADDER[pick.step];
  return { mode: 'auto', level: spec.env, step: pick.step, spec, reason: pick.reason };
}

// ── 조절기 ─────────────────────────────────────────────

/** 판정 창(ms)과 문턱 */
const WINDOW_MS = 2000;
const SETTLE_MS = 1000;
const DOWN_RATIO = 1.2;
const DOWN2_RATIO = 2.0;
const UP_RATIO = 1.05;
const CLEAR_RATIO = 0.7;
const UP_WAIT_MS = 6000;
const UP_WAIT_CLEAR_MS = 3000;
const UP_WAIT_MAX_MS = 120000;
/** 올린 뒤 이 시간 안에 다시 내려오면 그 단계로 다시 오르는 대기를 두 배로 늘린다 */
const PROBE_FAIL_MS = 5000;
const STABLE_SAVE_MS = 10000;
const RING = 512;

export interface GovernorState {
  step: number;
  label: string;
  targetMs: number;
  p90: number;
  changes: number;
  cpuBound: boolean;
  cpuP50: number;
}

/**
 * 프레임 시간 기반 자동 품질 조절. push(ms, now)를 매 프레임 부르고, 바뀐 단계 번호(또는 -1)를 받는다.
 * 목표는 화면 주사율 기준이되 최소 16.7ms(60fps): 120·180Hz 화면에서도 60fps를 지키면 충분하다.
 * 30·50Hz처럼 느린 화면은 관측된 최소 간격(주사율)을 목표로 삼는다(최대 33.3ms).
 */
export class QualityGovernor {
  step: number;
  targetMs = 1000 / 60;
  p90 = 0;
  changes = 0;
  /** 변경 기록(QA): [시각 ms, 이전, 이후, p90] */
  readonly history: number[][] = [];
  private readonly ring = new Float32Array(RING);
  private readonly cpuRing = new Float32Array(RING);
  private readonly when = new Float64Array(RING);
  private readonly scratch = new Float32Array(RING);
  private readonly scratch2 = new Float32Array(RING);
  private n = 0;
  private head = 0;
  private holdUntil = 0;
  private goodSince = -1;
  private lastChange = 0;
  private lastUpFrom = -1;
  private stableSince = 0;
  private savedStep: number;
  /** 단계별 '이 단계로 올라가기' 대기(ms) */
  private readonly upWait = new Float32Array(QUALITY_LADDER.length).fill(UP_WAIT_MS);
  private refreshMs = 1000 / 60;
  private minSeen = 1e9;
  private minCount = 0;
  enabled = true;
  /** 마지막 판정에서 CPU 병목이었는지(QA) */
  cpuBound = false;
  cpuP50 = 0;

  constructor(start: number, private readonly backend: string, private readonly persist = true) {
    this.step = start;
    this.savedStep = start;
  }

  /** 장면 전환·컴파일처럼 예상된 끊김 뒤에는 판정을 잠깐 멈춘다. */
  hold(now: number, ms = SETTLE_MS): void {
    this.holdUntil = Math.max(this.holdUntil, now + ms);
    this.n = 0;
    this.goodSince = -1;
  }

  /**
   * @param cpuMs 이 프레임 렌더 함수의 JS 시간(ms). CPU 병목 판정에 쓴다
   * @returns 새 단계 번호, 바뀌지 않았으면 -1
   */
  push(frameMs: number, now: number, cpuMs = 0): number {
    if (!this.enabled || !(frameMs > 0) || frameMs > 1000) return -1;
    // 주사율 추정: 가장 짧은 간격 근처가 여러 번 보이면 그것이 화면 주기다(느린 화면 대응)
    if (frameMs < this.minSeen * 0.97) { this.minSeen = frameMs; this.minCount = 0; }
    else if (frameMs < this.minSeen * 1.1) this.minCount++;
    if (this.minCount > 30) this.refreshMs = this.minSeen;
    this.targetMs = Math.max(1000 / 60, Math.min(1000 / 30, this.refreshMs));
    if (now < this.holdUntil) return -1;
    this.ring[this.head] = frameMs;
    this.cpuRing[this.head] = cpuMs;
    this.when[this.head] = now;
    this.head = (this.head + 1) % RING;
    if (this.n < RING) this.n++;
    // 창 안의 표본만 모아 p90
    let k = 0;
    for (let i = 0; i < this.n; i++) {
      const j = (this.head - 1 - i + RING) % RING;
      if (now - this.when[j] > WINDOW_MS) break;
      this.scratch2[k] = this.cpuRing[j];
      this.scratch[k++] = this.ring[j];
    }
    const span = k > 0 ? now - this.when[(this.head - k + RING) % RING] + this.ring[(this.head - k + RING) % RING] : 0;
    if (span < WINDOW_MS * 0.9 || k < 8) return -1;
    this.p90 = selectNth(this.scratch, k, Math.min(k - 1, Math.floor(k * 0.9)));
    const p50 = selectNth(this.scratch, k, k >> 1);
    this.cpuP50 = selectNth(this.scratch2, k, k >> 1);
    // 히스테리시스: 0.6 이상이면 CPU 병목, 0.45 아래로 내려가야 풀린다
    this.cpuBound = this.cpuP50 >= p50 * (this.cpuBound ? CPU_BOUND_RATIO * 0.75 : CPU_BOUND_RATIO);
    const t = this.targetMs;
    const max = this.cpuBound ? GPU_ONLY_STEP - 1 : QUALITY_LADDER.length - 1;
    // CPU 병목인데 해상도 전용 단계에 있으면 한 단계 되돌린다(흐려지기만 한다)
    if (this.step > max) return this.change(max, now);
    // 내리기
    if (this.p90 > t * DOWN_RATIO && this.step < max) {
      const by = this.p90 > t * DOWN2_RATIO ? 2 : 1;
      // 방금 올린 단계에서 곧바로 무너졌다면 그 단계로 다시 오르는 대기를 늘린다
      if (this.lastUpFrom >= 0 && now - this.lastChange < PROBE_FAIL_MS) {
        this.upWait[this.step] = Math.min(UP_WAIT_MAX_MS, this.upWait[this.step] * 2);
      }
      this.lastUpFrom = -1;
      return this.change(Math.min(max, this.step + by), now);
    }
    // 올리기: 여유가 길게 이어질 때 한 단계씩
    if (this.p90 <= t * UP_RATIO && this.step > 0) {
      if (this.goodSince < 0) this.goodSince = now;
      const wait = this.p90 <= t * CLEAR_RATIO ? Math.min(UP_WAIT_CLEAR_MS, this.upWait[this.step - 1]) : this.upWait[this.step - 1];
      if (now - this.goodSince >= wait) {
        this.lastUpFrom = this.step;
        return this.change(this.step - 1, now);
      }
    } else this.goodSince = -1;
    // 안정 단계 기억
    if (this.persist && this.step !== this.savedStep && now - this.stableSince >= STABLE_SAVE_MS) {
      this.savedStep = this.step;
      saveStoredStep(this.backend, this.step);
    }
    return -1;
  }

  private change(to: number, now: number): number {
    const from = this.step;
    this.step = to;
    this.changes++;
    this.lastChange = now;
    this.stableSince = now;
    this.history.push([Math.round(now), from, to, Math.round(this.p90 * 10) / 10]);
    if (this.history.length > 64) this.history.shift();
    this.hold(now);
    return to;
  }

  state(): GovernorState {
    return { step: this.step, label: QUALITY_LADDER[this.step]?.label ?? '', targetMs: this.targetMs, p90: this.p90, changes: this.changes, cpuBound: this.cpuBound, cpuP50: this.cpuP50 };
  }
}

/** GPU 장치 유실 후 재시작이면 'webgl'이 기록돼 있다. */
export const RENDERER_SESSION_KEY = 'dungeonhero.renderer';

/** a[0..n) 안에서 nth번째로 작은 값(제자리 quickselect, 할당 없음). a의 순서는 바뀐다. */
export function selectNth(a: Float32Array, n: number, nth: number): number {
  let lo = 0, hi = n - 1;
  while (lo < hi) {
    const pivot = a[(lo + hi) >> 1];
    let i = lo, j = hi;
    while (i <= j) {
      while (a[i] < pivot) i++;
      while (a[j] > pivot) j--;
      if (i <= j) { const t = a[i]; a[i] = a[j]; a[j] = t; i++; j--; }
    }
    if (nth <= j) hi = j;
    else if (nth >= i) lo = i;
    else return a[nth];
  }
  return a[nth];
}

export function shouldForceWebGL(explicit?: boolean): boolean {
  if (explicit) return true;
  try {
    if (typeof sessionStorage !== 'undefined' && sessionStorage.getItem(RENDERER_SESSION_KEY) === 'webgl') return true;
  } catch {
    /* 저장소 접근 불가 */
  }
  const url = typeof location !== 'undefined' ? new URLSearchParams(location.search).get('renderer') : null;
  if (url === 'webgl') return true;
  return loadPrefs().renderer === 'webgl';
}

